import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { ImagePlus, Loader2, X, Film } from 'lucide-react';
import { API } from './constants';

const CHUNK = 5 * 1024 * 1024; // 5MB

export const MediaManager = ({ planId, ensurePlanId }) => {
  const { token, getAuthHeaders } = useAuth();
  const inputRef = useRef(null);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(null); // { name, pct }

  const fetchMedia = useCallback(async (pid) => {
    if (!pid) { setMedia([]); return; }
    try {
      const res = await axios.get(`${API}/content/media?plan_id=${pid}`, getAuthHeaders());
      setMedia(res.data || []);
    } catch (e) { console.error(e); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchMedia(planId); }, [planId, fetchMedia]);

  const uploadOne = async (file, pid) => {
    const uploadId = (window.crypto?.randomUUID?.()) || `${Date.now()}-${Math.random()}`;
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    for (let i = 0; i < total; i++) {
      const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
      const fd = new FormData();
      fd.append('chunk', blob, 'chunk');
      await axios.post(`${API}/content/media/chunk?upload_id=${uploadId}&index=${i}`, fd, {
        headers: { ...getAuthHeaders().headers },
        onUploadProgress: (evt) => {
          const frac = evt.total ? evt.loaded / evt.total : 0;
          const pct = Math.round(((i + frac) / total) * 100);
          setUploading({ name: file.name, pct: Math.min(99, pct) });
        },
      });
    }
    await axios.post(`${API}/content/media/complete`, {
      upload_id: uploadId, plan_id: pid, filename: file.name,
      content_type: file.type, total_chunks: total,
    }, getAuthHeaders());
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = '';
    if (files.length === 0) return;
    const pid = planId || (ensurePlanId ? await ensurePlanId() : null);
    if (!pid) return;
    for (const file of files) {
      if (!(file.type.startsWith('image/') || file.type.startsWith('video/'))) {
        toast.error(`${file.name}: only photos and videos are allowed`);
        continue;
      }
      try {
        setUploading({ name: file.name, pct: 0 });
        await uploadOne(file, pid);
        toast.success(`${file.name} added`);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    setUploading(null);
    fetchMedia(pid);
  };

  const remove = async (mid) => {
    try {
      await axios.delete(`${API}/content/media/${mid}`, getAuthHeaders());
      setMedia((m) => m.filter((x) => x.media_id !== mid));
    } catch (e) { toast.error('Failed to remove'); }
  };

  const urlFor = (m) => `${API}/content/media/${m.media_id}/download?auth=${token}`;

  return (
    <div className="space-y-2" data-testid="media-manager">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Photos & Videos</label>
        <input ref={inputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFiles} data-testid="media-file-input" />
        <Button
          type="button" size="sm" variant="outline"
          className="border-sky-300 text-sky-600 h-8"
          disabled={!!uploading}
          onClick={() => inputRef.current?.click()}
          data-testid="media-add-btn"
        >
          <ImagePlus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      {media.length === 0 && !uploading && (
        <p className="text-xs text-muted-foreground italic">No media yet. Tap Add to attach photos or videos.</p>
      )}

      {media.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="media-grid">
          {media.map((m) => (
            <div key={m.media_id} className="relative group rounded-lg overflow-hidden border bg-slate-100 dark:bg-slate-800 aspect-square" data-testid={`media-item-${m.media_id}`}>
              {m.kind === 'video' ? (
                <video src={urlFor(m)} controls preload="metadata" className="w-full h-full object-cover" />
              ) : (
                <img src={urlFor(m)} alt={m.original_filename} className="w-full h-full object-cover" />
              )}
              {m.kind === 'video' && (
                <span className="absolute bottom-1 left-1 bg-black/60 text-white rounded px-1 py-0.5 flex items-center gap-0.5 text-[9px]">
                  <Film className="w-3 h-3" /> video
                </span>
              )}
              <button
                type="button"
                onClick={() => remove(m.media_id)}
                className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-1"
                data-testid={`media-delete-${m.media_id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {uploading && (
        <div className="space-y-1" data-testid="media-uploading">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span className="truncate">Uploading {uploading.name}… {uploading.pct}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div className="h-full bg-sky-500 transition-all" style={{ width: `${uploading.pct}%` }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaManager;
